/* eslint-disable @typescript-eslint/no-explicit-any */

// Imports for external libraries go here.
import React, { FC, useEffect, useRef, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { SuitesFilterPanelProps } from './SuitesFilterPanel.types';
import { StyledSuitesFilterPanel } from './SuitesFilterPanel.styles';
import {
  CheckboxControlsList,
  Messages,
  Modal,
  RadiobuttonControlsList,
  ToggleControlsList,
} from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { BrandFilter } from '../BrandFilter';
import {
  getUpdatedFiltersData,
  getCurrentSuites,
  totalFilterCount,
  useViewport,
  updateURLParams,
  getSubstringAfterFirst,
} from '../../modules/helpers';
import { COL_LENGTH, FILTER_PARAMS, FILTER_TYPES, MOBILE_COL_LENGTH } from '../../modules/constants/constant';
import { useSession } from '@marriott/mi-headless-utils';

// Use named rather than default exports.
export const SuitesFilterPanel: FC<SuitesFilterPanelProps> = ({
  props,
  popupOpenState,
  setPopupOpenState,
  setFilterButtonClicked,
  filterButtonClicked,
  filtersData,
  setFiltersData,
  filtersPayload,
  setFiltersPayload,
  setSuitesData,
  setFiltersCount,
  data,
  isLogoFlagEnabled,
}) => {
  const prevfiltersRef = useRef<any>([]);
  const filtersRef = useRef<any>();
  //checking for viewport
  const viewPort = useViewport();

  filtersRef.current = filtersPayload;
  const prevfiltersRefVal = filtersRef?.current;

  const [defaultSelectedFilters, setDefaultSelectedFilters] = useState<any>(filtersPayload);
  const [activeFiltersNotAvailable, setActiveFiltersNotAvailable] = useState<any>(false);
  const { updateSession } = useSession();

  /**
   *
   * @param payload | filter's payload
   * @returns | return's the updated available filter's data...
   */
  const updatedFiltersRes = (payload: any) => {
    // Get updated filters
    const activeFilters = payload?.filter((f: { dimensions: string[] }) => f?.dimensions && f?.dimensions?.length > 0);
    const updatedFilters =
      activeFilters?.length !== 0 ? getUpdatedFiltersData(data?.filters, payload, data?.suites) : data?.filters;
    return updatedFilters;
  };

  useEffect(() => {
    prevfiltersRef.current = filtersPayload;

    /**
     * give's the updated filter's response...
     */
    const updatedFilters = updatedFiltersRes(filtersPayload);

    setFiltersData(updatedFilters);
  }, []);

  const handleFiltersSelectionChange = (code: string, filterObjList: any[]) => {
    let temp: Array<string> = [];

    /**
     * setting up the filterCode's for forming the query params for deeplink.....
     */
    let filterCodes: Array<string> = [];

    if (code === FILTER_TYPES.BRANDS) {
      filterObjList?.forEach((obj: { brandTagId: string }) => {
        temp?.push(obj?.brandTagId);
      });

      filterCodes = [...new Set(temp)];
      temp = [...new Set(temp)];
    } else {
      filterObjList?.forEach((obj: { code: any; id: string }) => {
        temp?.push(obj?.code);
        obj?.code !== FILTER_TYPES.ANY_BEDROOMS && filterCodes?.push(getSubstringAfterFirst(obj?.code));
      });
    }
    const payload = defaultSelectedFilters?.map((obj: { type: string }) =>
      obj?.type === code ? { ...obj, dimensions: temp, codes: filterCodes } : obj
    );

    /**
     * Getting the updated Filter's...
     */
    const updatedFilters = updatedFiltersRes(payload);
    setFiltersData(updatedFilters);
    setFiltersPayload(payload);
    setDefaultSelectedFilters(payload);

    /***
     * Getting the Current Available Suite's
     */
    const currentSuites = getCurrentSuites(payload, data?.suites);

    /***
     * setting the error message if there are no result's...
     */
    currentSuites?.length !== 0 ? setActiveFiltersNotAvailable(false) : setActiveFiltersNotAvailable(true);
  };

  const updateSessionObj = async (filtersData: { type: string; dimensions: Array<string> }[], filterCnt: number) => {
    const sessionObj: any = {
      data: {
        LuxurySuites: {
          filters: filtersData,
          filtersCount: filterCnt,
        },
      },
    };
    await updateSession(sessionObj);
  };

  // set focus on filters pill on close of modal
  const getFocusOnFiltersPill = () => {
    if (filterButtonClicked?.AllFilters) {
      const input = document.getElementById('all filters');
      if (input) {
        input.focus();
      }
    } else if (filterButtonClicked?.Location) {
      const input = document.getElementById('mi-luxury-spa:country');
      if (input) {
        input.focus();
      }
    } else if (filterButtonClicked?.Brands) {
      const input = document.getElementById('mi-luxury-spa:brands');
      if (input) {
        input.focus();
      }
    } else if (filterButtonClicked?.Bedrooms) {
      const input = document.getElementById('mi-luxury-spa:bedrooms');
      if (input) {
        input.focus();
      }
    }
  };

  // apply filters on click of apply button on modal
  const applyBtnHandler = () => {
    setFiltersPayload(defaultSelectedFilters);

    // Calculate total filter count
    const filterCount = totalFilterCount(defaultSelectedFilters);
    setFiltersCount(filterCount);
    const currentSuites = getCurrentSuites(filtersPayload, data?.suites);

    setSuitesData(currentSuites);

    setFilterButtonClicked({ AllFilters: false, Brands: false, Location: false, Bedrooms: false });
    defaultSelectedFilters.map((filter: { type: string; dimensions: any; codes: string[] }) => {
      if (filter.type === FILTER_TYPES.BRANDS) {
        updateURLParams(FILTER_PARAMS.BRANDS_PARAM, filter.codes);
      } else if (filter.type === FILTER_TYPES.COUNTRY) {
        updateURLParams(FILTER_PARAMS.LOCATIONS_PARAM, filter.codes);
      } else if (filter.type === FILTER_TYPES.BEDROOMS) {
        updateURLParams(FILTER_PARAMS.BEDROOMS_PARAM, filter.codes);
      } else if (filter.type === FILTER_TYPES.CONNECTING_ROOMS) {
        updateURLParams(FILTER_PARAMS.CONNECTING_ROOMS_PARAM, filter.codes);
      }
    });

    /**
     * Focusing on the pills when the modal get's closed.....
     */
    filterButtonClicked && getFocusOnFiltersPill();

    updateSessionObj(defaultSelectedFilters, filterCount);
  };

  // reset the filter payload on modal
  const clearBtnHandler = () => {
    // reset the default selected filters
    const resetFilterPayload = defaultSelectedFilters?.map(
      (sf: { type: string; dimensions: string[]; codes: string[] }) => {
        const hasActiveDimensions = sf?.dimensions?.length !== 0;

        if (filterButtonClicked?.AllFilters) {
          return {
            type: sf?.type,
            dimensions: sf?.type === FILTER_TYPES.BEDROOMS && hasActiveDimensions ? [FILTER_TYPES.ANY_BEDROOMS] : [],
            codes: [],
          };
        }

        if (filterButtonClicked?.Location) {
          return sf?.type === FILTER_TYPES.COUNTRY && hasActiveDimensions
            ? { type: sf?.type, dimensions: [], codes: [] }
            : sf;
        }

        if (filterButtonClicked?.Bedrooms) {
          return sf?.type === FILTER_TYPES.BEDROOMS && hasActiveDimensions
            ? { type: sf?.type, dimensions: [FILTER_TYPES.ANY_BEDROOMS], codes: [] }
            : sf;
        }

        return sf?.type === FILTER_TYPES.BRANDS && hasActiveDimensions
          ? { type: sf?.type, dimensions: [], codes: [] }
          : sf;
      }
    );

    const updatedFilters = updatedFiltersRes(resetFilterPayload);

    /**
     * setting the updated filter's which in turn used to display the quickfilter's ....
     */
    setFiltersData(updatedFilters);

    setDefaultSelectedFilters(resetFilterPayload);
    setFiltersPayload(resetFilterPayload);
    setActiveFiltersNotAvailable(false);
    const filterCount = totalFilterCount(resetFilterPayload);
    updateSessionObj(resetFilterPayload, filterCount);
  };

  // show default selected filters on modal
  const showDefaultSelectedFilters = (filterData: any): any => {
    const result: any[] = [];

    if (!filterData?.data || !defaultSelectedFilters) {
      return result;
    }

    // Create a map for faster lookup of default filters by type
    const filterMap = new Map<string, string[]>();
    defaultSelectedFilters?.forEach((filter: { dimensions: string[]; type: string }) => {
      if (filter?.dimensions?.length) {
        filterMap?.set(filter?.type, filter?.dimensions);
      }
    });

    filterData?.data?.forEach((data: any) => {
      // Handle brand filters
      const brandDimensions = filterMap?.get(FILTER_TYPES.BRANDS);
      if (brandDimensions && data?.brands) {
        data?.brands?.forEach((brand: { brandTagId: string; label: string }) => {
          if (brandDimensions?.includes(brand?.brandTagId)) {
            result?.push({
              brandTagId: brand?.brandTagId,
              brandTagTitle: brand?.label,
            });
          }
        });
      }

      // Handle other filter types
      filterMap?.forEach((dimensions, type) => {
        if (type !== FILTER_TYPES.BRANDS && dimensions?.includes(data?.code)) {
          result?.push({
            code: data?.code,
            label: data?.label,
          });
        }
      });
    });

    return result;
  };

  // reset the filters payload to it's previous state on close of modal
  const resetFiltersOnClose = () => {
    if (prevfiltersRef?.current !== filtersPayload) {
      setFiltersPayload(prevfiltersRef?.current);
    }
  };

  // reset the filters payload to it's previous state on click outside of modal
  const resetFiltersOnClickOutside = () => {
    if (filtersRef?.current !== prevfiltersRefVal) {
      setFiltersPayload(prevfiltersRefVal);
    }
  };

  // show filters list on modal how it returns in UXL response with specific type
  const showFiltersList = (
    filterData: { type: string; filterName: string; data: any },
    idx: number,
    filterLabel: any,
    quickFilters: any
  ) => {
    switch (filterData?.type) {
      case FILTER_TYPES.BRANDS:
        return filterButtonClicked?.Brands || filterButtonClicked?.AllFilters ? (
          <BrandFilter
            key={idx}
            title={filterButtonClicked?.AllFilters ? filterLabel?.brandsLabel : ''}
            showSeparator={filterButtonClicked?.brands ? false : true}
            brandCategories={filterData?.data}
            defaultSelected={showDefaultSelectedFilters(filterData)}
            onChange={(obj): void => handleFiltersSelectionChange(filterData?.type, obj)}
            isHotelBrandSelectedFlag={true}
            clickTrackingLoc={'Filters'}
            clickTrackingVal={`${filterData?.filterName} Section:`}
            featureFlagLogo={isLogoFlagEnabled}
          />
        ) : null;

      case FILTER_TYPES.COUNTRY:
        return (filterButtonClicked?.Location || filterButtonClicked?.AllFilters) &&
          filterData?.data?.length > 0 &&
          !activeFiltersNotAvailable &&
          props?.filterLabelMappings?.countryLabel ? (
          <CheckboxControlsList
            key={idx}
            name={filterLabel?.countryLabel ? filterLabel?.countryLabel : filterData?.filterName}
            title={filterButtonClicked?.AllFilters ? filterLabel?.countryLabel : ''}
            controls={filterData?.data}
            enableShowMoreToggle={filterButtonClicked?.Location ? false : true}
            onChange={(obj): void => handleFiltersSelectionChange(filterData?.type, obj)}
            showMoreCount={props?.showMoreCount}
            showSeparator={true}
            clickTrackingLoc={'Filters'}
            clickTrackingVal={`${filterData?.filterName} Section:`}
            showMoreLabel={props?.showMoreLabel}
            showLessLabel={props?.showLessLabel}
            defaultSelected={showDefaultSelectedFilters(filterData)}
            colLength={viewPort === 'Mobile' ? MOBILE_COL_LENGTH : COL_LENGTH}
          />
        ) : null;

      case FILTER_TYPES.BEDROOMS:
        return (filterButtonClicked?.Bedrooms || filterButtonClicked?.AllFilters) &&
          filterData?.data?.length > 0 &&
          !activeFiltersNotAvailable &&
          props?.filterLabelMappings?.bedroomsLabel ? (
          <RadiobuttonControlsList
            key={idx}
            name={filterLabel?.bedroomsLabel ? filterLabel?.bedroomsLabel : filterData?.filterName}
            title={filterButtonClicked?.AllFilters ? filterLabel?.bedroomsLabel : ''}
            controls={filterData?.data}
            enableShowMoreToggle={true}
            onChange={(obj): void => handleFiltersSelectionChange(filterData?.type, obj)}
            showMoreCount={props?.showMoreCount}
            showSeparator={true}
            defaultSelected={showDefaultSelectedFilters(filterData)}
            custom_click_track_value={`Filters | ${filterData?.filterName} Section | internal`}
            colLength={viewPort === 'Mobile' ? MOBILE_COL_LENGTH : COL_LENGTH}
          />
        ) : null;
      case FILTER_TYPES.CONNECTING_ROOMS: {
        return (
          filterButtonClicked?.AllFilters &&
          filterData?.data?.length > 0 &&
          !activeFiltersNotAvailable && (
            <ToggleControlsList
              key={idx}
              name={quickFilters[0]?.filterTitle ? quickFilters[0]?.filterTitle : filterData?.filterName}
              title={quickFilters[0]?.filterTitle ? quickFilters[0]?.filterTitle : filterData?.filterName}
              controls={filterData?.data}
              enableShowMoreToggle={true}
              onChange={(obj): void => handleFiltersSelectionChange(filterData?.type, obj)}
              showMoreCount={props?.showMoreCount}
              custom_click_track_value={`Filters | ${filterData?.filterName} Section | internal`}
              showSeparator={true}
              defaultSelected={showDefaultSelectedFilters(filterData)}
            />
          )
        );
      }
      default:
        return null;
    }
  };

  return (
    <StyledSuitesFilterPanel data-testid="luxury-SuitesFilterPanel" data-component-name="m-luxury-SuitesFilterPanel">
      <Modal
        className={clsx('custom-scrollbar', 'modal-main-container', popupOpenState ? '' : 'close-popup-header')}
        show={true}
        handleBlur={true}
        popupOpenState={popupOpenState}
        disableScrollOnBody={true}
        setPopupOpenState={setPopupOpenState}
        allFilterPopUp={true}
        role="dialog"
        aria-labelledby={props?.filtersHeaderLabel}
        eventClass="radio-input checkbox-input"
        onClose={(): void => {
          resetFiltersOnClickOutside();
          setPopupOpenState(!popupOpenState);
          setFilterButtonClicked({ AllFilters: false, Brands: false, Location: false, Bedrooms: false });
          resetFiltersOnClickOutside();
        }}
        modalId={props?.filtersHeaderLabel}
        aria-label="Suites Filter Options"
      >
        <Modal.Header
          className={clsx('modal-header')}
          labelText={
            filterButtonClicked?.AllFilters
              ? props?.allFiltersLabel
              : filterButtonClicked?.Brands
              ? props?.filterLabelMappings?.brandsLabel
              : filterButtonClicked?.Bedrooms
              ? props?.filterLabelMappings?.bedroomsLabel
              : filterButtonClicked?.Location && props?.filterLabelMappings?.countryLabel
          }
          popupHeaderOnCLoseFunc={(): void => {
            resetFiltersOnClose();
            setPopupOpenState(!popupOpenState);
            /**
             * Focusing on the pills when the modal get's closed.....
             */
            filterButtonClicked && getFocusOnFiltersPill();
            setFilterButtonClicked({ AllFilters: false, Brands: false, Location: false, Bedrooms: false });
          }}
        />
        {activeFiltersNotAvailable && (
          <div className={clsx('container', 'mt-3 px-2 px-md-5')}>
            <Messages messageType={'warning'} messageHeading={props?.noResultsErrorMessage} />
          </div>
        )}
        <Modal.Body className={clsx('all-filters', 'custom-scrollbar')}>
          {filtersData?.map((filterData: any, index: number) =>
            showFiltersList(filterData, index, props?.filterLabelMappings, props?.quickFilters)
          )}
        </Modal.Body>

        <Modal.Footer
          clearClickHandler={clearBtnHandler}
          setPopUpOpenState={setPopupOpenState}
          applyBtnClickHandler={applyBtnHandler}
          clearButtonLabel={props?.clearLabel}
          applyLabel={props?.applyLabel}
          applyButtonDisabled={activeFiltersNotAvailable ?? false}
          className={clsx('modal-footer')}
          clickTrackingLoc={'Suites_Filter_Modal'}
          clearBtnClassname={'m-link'}
        />
      </Modal>
    </StyledSuitesFilterPanel>
  );
};
